import {
  IAccountRespSchema,
  IAddressSchema,
  LegalEntityAddressFormatEnum,
  MCustomSelectProps,
} from '@monetize/types/app';
import React, { useEffect, useState } from 'react';
import { useGetAccountById } from '../../api/accountsService';
import { toTitleCase } from '../../utils';
import { getAddress } from '../../utils/address';
import { MFlex, MText, MTooltip } from '../Monetize';
import { MCustomSelect } from '../Monetize/MCustomSelect/MCustomSelect';
import { MainSearchInputV2 } from '../Monetize/MCustomSelect/components/MainSearchInputV2';
import { Highlighter } from '../Monetize/MPageSearchInput/Highlighter';

interface IAddressWithType {
  type: 'billing' | 'shipping';
  fullAddress: string;
  address: IAddressSchema;
  id: string;
  account?: IAccountRespSchema | null;
}
export const AccountAddressSelectRenderItem = ({
  item,
  query,
}: {
  item: IAddressWithType;
  query: string;
  account?: IAccountRespSchema | null;
}) => {
  const { address, fullAddress, account } = item;
  return (
    <MFlex
      direction="column"
      overflow="hidden"
      whiteSpace="nowrap"
      textOverflow="ellipsis"
      w="100%"
      textAlign="right"
    >
      {!address ? (
        <MText fontWeight="medium" fontStyle="italic">
          Add {toTitleCase((item as IAddressWithType).type)} Address
        </MText>
      ) : (
        <Highlighter
          title={fullAddress}
          color="tPurple.dark"
          fontSize="sm"
          fontWeight="medium"
          textToHighlight={fullAddress}
          searchWords={[query]}
          whiteSpace="normal"
        />
      )}

      <MText fontSize="xs" color="tGray.darkPurple">
        {account?.accountName}
      </MText>
      <MText fontSize="xs" color="tGray.darkPurple">
        {toTitleCase((item as IAddressWithType).type)} Address
      </MText>
    </MFlex>
  );
};

interface AccountAddressSelectProps extends MCustomSelectProps {
  accountId?: string | null;
  addressFormat?: LegalEntityAddressFormatEnum | null;
  isReadOnly?: boolean;
  trucatedReadonly?: boolean;
  addressType?: 'billing' | 'shipping';
}

export const AccountAddressSelect = React.forwardRef<
  any,
  AccountAddressSelectProps
>(
  (
    {
      accountId,
      addressFormat = LegalEntityAddressFormatEnum.US_FORMAT,
      onChange,
      value,
      isReadOnly,
      trucatedReadonly,
      addressType,
      inputProps,
      popOverContentProps,
      ...rest
    }: AccountAddressSelectProps,
    ref,
  ) => {
    const { data: accountDetails, isLoading: isAccountLoading } =
      useGetAccountById(accountId!, {
        enabled: !!accountId,
        refetchOnWindowFocus: false,
      });

    const [query, setQuery] = useState('');

    const [addresses, setAddresses] = useState<
      {
        type: string;
        id: string;
        address?: IAddressSchema | null;
        fullAddress: string;
        account?: IAccountRespSchema | null;
      }[]
    >([]);
    useEffect(() => {
      const addressesArr = [
        {
          type: 'billing',
          address: accountDetails?.billingAddress,
          account: accountDetails,
        },
        {
          type: 'shipping',
          address: accountDetails?.shippingAddress,
          account: accountDetails,
        },
      ];
      setAddresses(
        addressesArr
          .filter(({ type }) => !addressType || type === addressType)
          .map(({ type, address, account }) => ({
            address,
            id: address?.id || type,
            type,
            fullAddress: getAddress(address, { addressFormat }).fullAddress,
            account,
          })),
      );
    }, [accountDetails, addressFormat, addressType]);

    const [iAddresses, setIAddresses] = useState(addresses);
    useEffect(() => {
      if (query.trim()) {
        setIAddresses(
          addresses?.filter(({ fullAddress }) =>
            fullAddress.toLowerCase().includes(query.toLowerCase()),
          ),
        );
      } else {
        setIAddresses(addresses);
      }
    }, [addresses, query]);

    if (isReadOnly) {
      const address = addresses.find((addr) => addr.id === value);
      if (address) {
        return (
          <>
            {trucatedReadonly ? (
              <MTooltip label={address.fullAddress}>
                <MText lineClamp={1} truncate whiteSpace={'initial'}>
                  {address.fullAddress}
                </MText>
              </MTooltip>
            ) : (
              <MText>{address.fullAddress}</MText>
            )}

            {!!address.account?.accountName && (
              <MText fontSize="xs" color="tGray.darkPurple">
                {address.account?.accountName}
              </MText>
            )}
            <MText fontSize="xs" color="tGray.darkPurple">
              {toTitleCase(address.type)} Address
            </MText>
          </>
        );
      }

      return (
        <MText color="tGray.darkPurple" fontStyle="italic">
          Blank
        </MText>
      );
    }

    return (
      <MCustomSelect
        ref={ref}
        onChange={onChange}
        isLazy
        useMainInputAsSearchInput
        MainInputComponent={MainSearchInputV2}
        placeholder="Select"
        itemTitle="fullAddress"
        itemValue="id"
        items={iAddresses}
        loading={isAccountLoading}
        useHighlightedItemList
        hideHighlightSearchResultsCount
        showItemDivider
        inputProps={{
          autoComplete: 'off',
          ...inputProps,
        }}
        useExternalQuery
        externalQuery={query}
        onChangeQuery={setQuery}
        skipFilter={true}
        renderItemContent={AccountAddressSelectRenderItem}
        value={value}
        popOverContentProps={{
          width: '100%',
          minWidth: '20rem',
          ...popOverContentProps,
        }}
        {...rest}
      />
    );
  },
);
